import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClipboardList, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadCertificatePdf, downloadBulkCertificatesPdf } from "@/lib/pdf";

/**
 * ClassListPrint — طباعة قوائم بأسماء طلاب فصل واحد أو كل فصول الصف
 * معًا في ملف PDF واحد مُجمَّع (كل فصل يبدأ بصفحة جديدة).
 */
export default function ClassListPrint() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [cls, setCls] = useState("__all__");
  const [classes, setClasses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!grade) { setClasses([]); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  const generate = async () => {
    if (!grade) return toast.error("اختر الصف أولاً");
    setGenerating(true);
    try {
      const targetClasses = cls === "__all__" ? (classes.length ? classes : [""]) : [cls];
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      const { createRoot } = await import("react-dom/client");
      const nodes = [];
      for (const c of targetClasses) {
        const { data: students } = await api.get("/students", {
          params: { stage: ctx.stage, year: ctx.year, grade, ...(c ? { class_name: c } : {}) },
        });
        const sorted = [...students].sort((a, b) =>
          (parseInt(a.seat_number, 10) || 0) - (parseInt(b.seat_number, 10) || 0));

        const wrap = document.createElement("div");
        wrap.style.width = "794px";
        wrap.style.padding = "20px";
        wrap.style.background = "#ffffff";
        container.appendChild(wrap);
        const root = createRoot(wrap);
        await new Promise((resolve) => {
          root.render(
            <ClassListSheet school={settings} stage={ctx.stage} year={ctx.year}
                             grade={grade} cls={c} students={sorted} />
          );
          document.fonts.ready.then(() => setTimeout(resolve, 150));
        });
        nodes.push(wrap);
      }

      if (nodes.length === 1) {
        await downloadCertificatePdf(nodes[0], `قائمة-${grade}-${cls === "__all__" ? "الكل" : cls}.pdf`);
      } else {
        await downloadBulkCertificatesPdf(nodes, `قوائم-${grade}.pdf`);
      }
      container.remove();
      toast.success("تم توليد ملف القوائم بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="classlist-page">
      <PageHeader
        icon={ClipboardList}
        overline="التقارير والشهادات"
        title="طباعة قوائم الفصول"
        subtitle="طباعة قوائم الطلاب لكل فصل بتنسيق موحّد وجاهز للطباعة أو التصدير PDF."
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[12px]">الصف</Label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="h-10" data-testid="cl-grade"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
            <SelectContent>
              {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px]">الفصل</Label>
          <Select value={cls} onValueChange={setCls} disabled={!grade}>
            <SelectTrigger className="h-10" data-testid="cl-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">كل الفصول (ملف واحد مُجمَّع)</SelectItem>
              {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={generate} disabled={!grade || generating} className="h-10 w-full" data-testid="cl-generate">
            <FileDown className="h-4 w-4 me-1.5" /> {generating ? "جارٍ التوليد…" : "تنزيل PDF"}
          </Button>
        </div>
      </div>

      {!grade && (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف لعرض خيارات الطباعة
        </div>
      )}
    </div>
  );
}

/** ورقة قائمة الفصل — مُصمَّمة للطباعة/التصدير فقط (تُلتقَط كصورة). */
function ClassListSheet({ school, stage, year, grade, cls, students }) {
  return (
    <div className="mx-auto max-w-4xl bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm">
      <div className="border-b-2 border-primary/50 pb-4 text-center space-y-1 relative">
        {school?.ministry_logo && (
          <img src={school.ministry_logo} alt="شعار الوزارة"
               className="absolute top-0 start-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        {school?.school_logo && (
          <img src={school.school_logo} alt="شعار المدرسة"
               className="absolute top-0 end-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        <div className="text-primary font-display text-xl font-bold">{school?.ministry_name || "وزارة التعليم"}</div>
        {school?.education_dept && <div className="text-primary font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-primary font-medium">{school.office_name}</div>}
        <div className="text-primary font-display text-lg font-bold">{school?.school_name || "المدرسة"}</div>
      </div>

      <div className="mt-6 text-center">
        <h2 className="font-display text-lg font-bold text-primary">قائمة بأسماء الطلاب</h2>
        <div className="mt-2 text-sm">
          {stage} — الصف: {grade} — الفصل: {cls || "الكل"}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">العام الدراسي: {year}</div>
      </div>

      <table className="w-full mt-6 text-sm border-collapse">
        <thead>
          <tr className="bg-primary text-white">
            <th className="px-2 py-2 text-center border border-primary w-12">م</th>
            <th className="px-2 py-2 text-start border border-primary">اسم الطالب</th>
            <th className="px-2 py-2 text-center border border-primary w-40">ملاحظات</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s, i) => (
            <tr key={s.id || i}>
              <td className="px-2 py-2 border border-slate-200 text-center">{i + 1}</td>
              <td className="px-2 py-2 border border-slate-200">{s.name}</td>
              <td className="px-2 py-2 border border-slate-200"></td>
            </tr>
          ))}
          {students.length === 0 && (
            <tr><td colSpan={3} className="px-2 py-6 border border-slate-200 text-center text-muted-foreground">
              لا يوجد طلاب في هذا التحديد
            </td></tr>
          )}
        </tbody>
      </table>

      <div className="mt-8 text-sm text-muted-foreground">إجمالي عدد الطلاب: {students.length}</div>
    </div>
  );
}
